import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { InvestmentType } from '@inversor-hub/types';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import PercentageField from '@/components/form/PercentageField';
import ProvincesSelector from '@/components/form/ProvincesSelector';
import { investmentTypeLabels } from '@/features/investments/constants';
import {
  useGetInvestorPreferences,
  useCreateInvestorPreferences,
  useUpdateInvestorPreferences,
} from '@/lib/api/investor-preferences';

interface QuickPreferencesModalProps {
  open: boolean;
  onComplete: () => void;
  onClose: () => void;
}

type Step = 'quick' | 'refine';

export default function QuickPreferencesModal({
  open,
  onComplete,
  onClose,
}: QuickPreferencesModalProps) {
  const [step, setStep] = useState<Step>('quick');

  // Step 1 state
  const [averageTicket, setAverageTicket] = useState<number | null>(null);
  const [investmentTypes, setInvestmentTypes] = useState<InvestmentType[]>([]);

  // Step 2 state
  const [expectedGrossReturn, setExpectedGrossReturn] = useState<number | null>(null);
  const [preferredProvinces, setPreferredProvinces] = useState<string[]>([]);
  const [requiresMortgage, setRequiresMortgage] = useState(false);

  const { data: existingPreferences } = useGetInvestorPreferences();
  const { mutate: createPreferences, isPending: isCreating } = useCreateInvestorPreferences();
  const { mutate: updatePreferences, isPending: isUpdating } = useUpdateInvestorPreferences();

  const isStep1Valid = !!averageTicket && investmentTypes.length > 0;

  const handleInvestmentTypeChange = (type: InvestmentType) => {
    setInvestmentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleStep1Submit = () => {
    const payload = {
      averageTicket: averageTicket!,
      desiredInvestmentTypes: investmentTypes,
      preferredProvinces: [],
      requiresMortgage: false,
    };

    const savePreferences = existingPreferences ? updatePreferences : createPreferences;

    savePreferences(payload, { onSuccess: () => setStep('refine') });
  };

  const handleStep2Submit = () => {
    updatePreferences(
      {
        averageTicket: averageTicket!,
        desiredInvestmentTypes: investmentTypes,
        preferredProvinces,
        requiresMortgage,
        expectedGrossReturn: expectedGrossReturn ?? 0,
      },
      { onSuccess: () => onComplete() }
    );
  };

  if (step === 'refine') {
    return (
      <Dialog
        open={open}
        onClose={onComplete}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <IconButton
            onClick={onComplete}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
            aria-label="Cerrar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <CheckCircleOutlineIcon color="success" />
            <Typography variant="h5" fontWeight={700}>
              ¡Compatibilidad activada!
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Afina aún más tus resultados con estos 3 campos opcionales.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            <PercentageField
              id="expectedGrossReturn"
              label="Rentabilidad bruta esperada (%)"
              placeholder="Ej: 8"
              value={expectedGrossReturn}
              onChange={setExpectedGrossReturn}
              allowDecimalScale
            />
            <ProvincesSelector
              value={preferredProvinces}
              onChange={setPreferredProvinces}
              label="Provincias de interés"
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                ¿Necesitas hipoteca?
              </Typography>
              <RadioGroup
                row
                value={requiresMortgage.toString()}
                onChange={(e) => setRequiresMortgage(e.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label="Sí" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleStep2Submit}
            disabled={isUpdating}
            fullWidth
          >
            {isUpdating ? 'Guardando...' : 'Guardar y ver oportunidades'}
          </Button>
          <Button variant="text" color="inherit" onClick={onComplete} fullWidth>
            Ir a las oportunidades
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
          aria-label="Cerrar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Descubre tu compatibilidad
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Rellena estos 2 campos y verás tu nivel de compatibilidad con cada oportunidad.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" gap={1} mb={3}>
          <Chip
            label="Alta"
            size="small"
            sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}
          />
          <Chip
            label="Media"
            size="small"
            sx={{ bgcolor: 'warning.main', color: 'warning.contrastText' }}
          />
          <Chip
            label="Baja"
            size="small"
            sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              ¿Cuánto capital tienes disponible?
            </Typography>
            <EurCurrencyField
              label="Capital disponible"
              placeholder="Ej: 150.000"
              value={averageTicket}
              onChange={setAverageTicket}
              allowDecimalScale={false}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              ¿Qué tipos de operación te interesan?
            </Typography>
            <FormGroup>
              {Object.entries(investmentTypeLabels).map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={investmentTypes.includes(value as InvestmentType)}
                      onChange={() => handleInvestmentTypeChange(value as InvestmentType)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleStep1Submit}
          disabled={!isStep1Valid || isCreating}
          fullWidth
        >
          {isCreating ? 'Guardando...' : 'Ver mi compatibilidad'}
        </Button>
        <Button variant="text" color="inherit" onClick={onClose} fullWidth>
          Saltar por ahora
        </Button>
      </DialogActions>
    </Dialog>
  );
}
